import {
  Box,
  Button,
  Checkbox,
  Grid,
  LinearProgress,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLoaderData, useParams } from "react-router-dom";
import { get } from "../../../apiClient";
import TablePagination from "../../../components/TablePagination";
import {
  action,
  fireUpdateEmail,
  isUrlGoodToSave,
  modalStyle,
  REJECTION_REASON_KEY,
  URL_STATUS_KEY,
} from "../../../utils/urlsTabsHelper";
import { buildEpisodeIndex, matchUrlRow } from "../utils/podcastShows";
import BulkEditModal from "./BulkEditModal";
import {
  EPISODE_NOUN,
  minimumSelectionHint,
  useBulkUrlSelection,
} from "./bulkUrlSelection";
import MultiCreativeRow from "./MultiCreativeRow";
import { BulkSelectionNotice, QueueSelectionControl } from "./SelectionBanner";

const DEFAULT_PAGINATION = { page: 0, perPage: 20 };
const DEFAULT_SEARCH_RESULT = {
  records: [],
  totalCount: 0,
  ...DEFAULT_PAGINATION,
};
const COLUMN_COUNT = 10;

const urlsTable = ({ campaignId, page, perPage, ...filters }) => {
  return new Promise((resolve, reject) => {
    const params = {
      page,
      perPage,
      ...filters,
    };
    get("managed_plus", `/campaigns/${campaignId}/campaign_urls`, { params })
      .then(({ result, total_count }) => {
        resolve({ records: result, totalCount: total_count, page, perPage });
      })
      .catch(reject);
  });
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: "#1976D214",
  },
}));

const ErrorRow = ({ error }) => {
  return (
    <StyledTableRow>
      <TableCell colSpan={COLUMN_COUNT} sx={{ textAlign: "center" }}>
        {error}
      </TableCell>
    </StyledTableRow>
  );
};

const EmptyRow = ({ isPendingUrl }) => {
  return (
    <StyledTableRow>
      <TableCell
        colSpan={COLUMN_COUNT}
        sx={{ textAlign: "center", color: "text.secondary" }}
      >
        {isPendingUrl ? "No pending episodes" : "No reviewed episodes"}
      </TableCell>
    </StyledTableRow>
  );
};

const MultiCreativesTable = ({
  searchOptions = {},
  refreshMethod,
  refreshFlag,
  enableEmailButtonMethod,
  enableEmailButtonVar,
  onForbiddenError,
  campaignStatus,
}) => {
  const [filters, setFilter] = useState({ ...DEFAULT_PAGINATION });
  // A request is issued on mount, so the queue starts out loading. Starting at
  // false would render the "no episodes" row for one frame before the first
  // response, which is the very ambiguity the empty state exists to remove.
  const [isLoading, setIsLoading] = useState(true);
  const [searchResult, setSearchResult] = useState(DEFAULT_SEARCH_RESULT);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const { status } = searchOptions;
  const isPendingUrl = status === "pending";
  const { page, perPage, records, error, totalCount } = searchResult;
  const { page: requestedPage, perPage: requestedPerPage } = filters;
  const { id: campaignId } = useParams();
  const { uid, account_email, podcast_shows } = useLoaderData();
  const [openModalBulkEdit, setOpenModalBulkEdit] = useState(false);
  const [bulkEditParams, setBulkEditParams] = useState({});
  const [bulkSaveButtonEnabled, setBulkSaveButtonEnabled] = useState(false);
  const [justSentUpdateEmail, setJustSentUpdateEmail] = useState(false);
  const [emailButtonLabel, setEmailButtonLabel] = useState("SEND EMAIL UPDATE");
  const [previewedArtworkUrl, setPreviewedArtworkUrl] = useState(null);
  const totalPages = Math.ceil(totalCount / perPage);
  const episodeIndex = useMemo(
    () => buildEpisodeIndex(podcast_shows),
    [podcast_shows]
  );
  const selection = useBulkUrlSelection({
    records,
    totalCount,
    campaignId,
    status,
    noun: EPISODE_NOUN,
    onForbiddenError,
  });
  // Both callbacks are recreated on every CreativeTab render, so they are kept
  // out of the request effect's dependencies to stop identity churn from
  // refetching.
  const callbacks = useRef({ enableEmailButtonMethod, onForbiddenError });

  useEffect(() => {
    callbacks.current = { enableEmailButtonMethod, onForbiddenError };
  });

  useEffect(() => {
    if (page >= totalPages) {
      setFilter((prevFilters) => ({
        ...prevFilters,
        page: Math.max(totalPages - 1, 0),
      }));
    }
  }, [page, totalPages]);

  async function sendEmailUpdate() {
    try {
      await fireUpdateEmail(campaignId, account_email, uid);
      setJustSentUpdateEmail(true);
      setEmailButtonLabel("EMAIL UPDATE SENT");
    } catch (error) {
      if ("status" in error && error.status === 403) {
        onForbiddenError && onForbiddenError();
        return;
      }

      throw error;
    }
  }

  const updateURLBundle = () =>
    selection.save({
      params: bulkEditParams,
      campaignId,
      campaignStatus,
      onSuccess: () => {
        handleClose();
        refreshMethod();
        setRefreshCounter((previous) => previous + 1);
      },
    });

  const bulkEditParameterUpdate = (parameter) => (e) => {
    const value = e;
    setBulkEditParams((prevParams) => {
      const updatedParams = {
        ...prevParams,
        [parameter]: value,
      };
      if (parameter === URL_STATUS_KEY && value !== "rejected") {
        updatedParams[REJECTION_REASON_KEY] = "";
      }
      setBulkSaveButtonEnabled(isUrlGoodToSave(updatedParams));
      return updatedParams;
    });
  };

  const updateURL = async (urlToEdit) => {
    let params = {
      id: urlToEdit.id,
      url_status_id: urlToEdit.status,
      url_content_rating_id: urlToEdit.url_content_rating_id,
    };

    if (urlToEdit.status === "rejected") {
      params = {
        ...params,
        rejection_reason: urlToEdit.rejection_reason,
      };
    }
    await action({
      urls_to_be_updated: JSON.stringify([params]),
      campaignId,
      campaign_status: campaignStatus,
    });
    selection.removeChecked(urlToEdit.id);
    if (isPendingUrl) refreshMethod();
    setRefreshCounter((previous) => previous + 1);
  };

  const handleCheckboxClick = selection.toggleRow;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    urlsTable({
      campaignId,
      status,
      page: requestedPage,
      perPage: requestedPerPage,
    })
      .then((result) => {
        if (cancelled) return;

        setSearchResult(result);
        if (status === "pending") {
          callbacks.current.enableEmailButtonMethod?.(result.totalCount > 0);
        }
      })
      .catch((requestError) => {
        if (cancelled) return;

        if (requestError.status === 403) {
          callbacks.current.onForbiddenError?.();
        }

        setSearchResult({
          ...DEFAULT_SEARCH_RESULT,
          error:
            "An error occurred while fetching the data. Please, contact support if the error is persistent.",
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    campaignId,
    status,
    requestedPage,
    requestedPerPage,
    refreshFlag,
    refreshCounter,
  ]);

  function handleClose() {
    setOpenModalBulkEdit(false);
    setBulkEditParams({});
    setBulkSaveButtonEnabled(false);
    selection.clearSaveError();
  }

  const handleCloseArtworkModal = () => setPreviewedArtworkUrl(null);

  return (
    <>
      <Typography variant="h6">
        {isPendingUrl ? "Pending review" : "Reviewed"}
      </Typography>
      <BulkSelectionNotice
        notice={selection.notice}
        onClose={selection.dismissNotice}
      />
      <Grid
        container
        sx={{ px: 2, py: 1.5, alignItems: "center", columnGap: 1 }}
      >
        {isPendingUrl ? (
          <Tooltip
            title={
              selection.selectedCount < 2
                ? minimumSelectionHint(EPISODE_NOUN)
                : ""
            }
          >
            <span>
              <Button
                variant="contained"
                disabled={selection.selectedCount < 2}
                onClick={() => setOpenModalBulkEdit(true)}
              >
                {selection.buttonLabel}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            disabled={enableEmailButtonVar || justSentUpdateEmail}
            onClick={(e) => sendEmailUpdate()}
          >
            {emailButtonLabel}
          </Button>
        )}
        {isPendingUrl && selection.showQueueControl && (
          <QueueSelectionControl
            label={selection.queueLabel}
            onSelectAll={selection.selectQueue}
            allSelected={selection.allPendingSelected}
            selecting={selection.selectingAll}
            error={selection.selectAllError}
            clearLabel={selection.clearLabel}
            onClear={selection.clearSelection}
            selectedCount={selection.selectedCount}
          />
        )}
      </Grid>
      <TableContainer sx={{ width: "100%" }}>
        {isLoading && <LinearProgress />}
        <Table size="small" sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              {isPendingUrl && totalCount > 0 ? (
                <TableCell>
                  <Checkbox
                    style={{ width: 20, height: 20 }}
                    checked={selection.headerChecked}
                    indeterminate={selection.headerIndeterminate}
                    onClick={selection.togglePage}
                    inputProps={{ "aria-label": selection.headerLabel }}
                  />
                </TableCell>
              ) : null}
              <TableCell style={{ minWidth: 180 }}>Content</TableCell>
              <TableCell style={{ width: 72, padding: "6px 10px" }}>
                Artwork
              </TableCell>
              <TableCell style={{ width: 96, padding: "6px 10px" }}>
                Start From
              </TableCell>
              <TableCell style={{ width: 200, padding: "6px 10px" }}>
                CTA
              </TableCell>
              <TableCell
                style={{ width: 113, height: 36, padding: "6px 10px" }}
              >
                Content Type
              </TableCell>
              <TableCell style={{ width: 103, padding: "6px 10px" }}>
                Status
              </TableCell>
              <TableCell style={{ width: 312, padding: "6px 16px 6px 10px" }}>
                {isPendingUrl ? "Content Rating" : "Content Category"}
              </TableCell>
              <TableCell style={{ width: 158, padding: "6px 24px 6px 10px" }}>
                Update Status
              </TableCell>
              <TableCell style={{ width: 240, padding: "6px 10px" }}>
                Rejection Reason
              </TableCell>
              <TableCell
                style={{ width: 57, padding: "6px 10px" }}
                align="right"
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {error ? (
              <ErrorRow error={error} />
            ) : records.length === 0 && !isLoading ? (
              <EmptyRow isPendingUrl={isPendingUrl} />
            ) : (
              records.map((item) => (
                <MultiCreativeRow
                  onCheck={handleCheckboxClick}
                  onSave={updateURL}
                  checked={selection.isChecked(item.id)}
                  checkable={item.status === "pending"}
                  loading={isLoading}
                  urlInfo={item}
                  key={item.id}
                  episode={matchUrlRow(item, episodeIndex)}
                  onPreviewArtwork={setPreviewedArtworkUrl}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!error && (
        <TablePagination
          totalCount={totalCount}
          filters={filters}
          setFilter={setFilter}
        />
      )}
      <BulkEditModal
        open={openModalBulkEdit}
        onClose={handleClose}
        onSave={updateURLBundle}
        onParamChange={bulkEditParameterUpdate}
        saveEnabled={bulkSaveButtonEnabled}
        params={bulkEditParams}
        selectedCount={selection.selectedCount}
        noun={EPISODE_NOUN}
        saving={selection.saving}
        error={selection.saveError}
      />
      <Modal
        open={Boolean(previewedArtworkUrl)}
        onClose={handleCloseArtworkModal}
        closeAfterTransition
      >
        <Box sx={modalStyle} alignItems="center">
          <Grid container>
            <Grid
              container
              item
              xs={12}
              justifyContent="center"
              alignItems="center"
            >
              <img
                alt="Podcast Artwork preview"
                src={previewedArtworkUrl ?? ""}
                style={{
                  maxHeight: "70vh",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            </Grid>
            <Grid container item xs={12} justifyContent="flex-end">
              <Button variant="text" onClick={handleCloseArtworkModal}>
                CLOSE
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default MultiCreativesTable;
